import React, {
  useCallback, useContext, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Alert, AlertTitle, Box, Table, TableContainer, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import { withAuthConsumer, mapStateAuth, AppContext } from '../../../providers/authProvider';
import api from '../../../api/req';
import {
  RemoveSelectionButton, GenerateButton, SaveButton, SelectAllButton, OKButton,
} from '../../../components/Styled/Buttons';
import { CommandPanel } from '../../../components/Styled/Misc';
import { EditorContainer } from '../../../components/Styled/Forms';
import Row from './rowWarnings';
import RowKDB from './rowKDB';
import Loader from '../../../components/MaterialUI/loader';

const MessageText = styled.span`
  font-weight: 700;
  color: #5e93d0;
  font-size: 24px;
  margin-left: 15px;
`;
const MessageContainer = styled.div`
  display: flex;
  align-items: center;
`;
const SpanKDB = styled.span`
  font-style: italic;
  color: #2d2828;
`;
const ContainerKDB = styled.div`
  margin-top: 10px;
  max-height: 50px;
  overflow-y: scroll;
  max-width: 100%;
  overflow-wrap: break-word;
  border: 1px solid #dce1e8;
  border-radius: 4px;
  padding: 5px;
  font-size: 12px;
`;

function Warnings({ currentOrg, currentDate }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [err, setErr] = useState(null);
  const [message, setMessage] = useState(null);
  const [resultKDB, setResultKDB] = useState(null);
  const [showKDB, setShowKDB] = useState(false);
  const [result, setResult] = useState(null);
  const { auth, currentUser } = useContext(AppContext);

  const startProcessing = useCallback(
    () => {
      const loader = async () => {
        const r = await api.post('/api/service/master/get_kdb_views/', auth, {
          org: currentOrg.id,
          date: currentDate,
        });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      };
      setIsProcessing(true);
      setErr(null);
      loader()
        .then((d) => {
          setResultKDB(d.map((dKDB) => ({
            ...dKDB,
            save: false,
          })));
          setShowKDB(true);
          if (!d.length) setMessage('Дані для обробки відсутні!');
          else setMessage(null);
        })
        .catch((e) => setErr(e.message))
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [auth, currentDate, currentOrg.id],
  );

  const getWarning = useCallback(
    () => {
      const loader = async () => {
        const r = await api.patch('/api/service/master/warnings/', auth, {
          org: currentOrg.id,
          date: currentDate,
          accounts: resultKDB.filter((res) => res.save === true)
            .map((res) => res.id),
        });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      };
      setIsProcessing(true);
      setErr(null);
      loader()
        .then((d) => {
          setResult(d.map((dW) => ({
            ...dW,
            save: false,
          })));
          setShowKDB(false);
          if (!d.length) setMessage('Дані для обробки відсутні!');
          else setMessage(null);
        })
        .catch((e) => setErr(e.message))
        .finally(() => setIsProcessing(false));
    },
    [auth, currentDate, currentOrg.id, resultKDB],
  );

  const saveChanges = useCallback(
    () => {
      const loader = async () => {
        const r = await api.post('/api/service/master/warnings/', auth, {
          to_save: result.filter((res) => res.save === true),
        });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return true;
      };
      setIsProcessing(true);
      setErr(null);
      loader()
        .then(() => {
          startProcessing();
        })
        .catch((e) => setErr(e.message))
        .finally(() => setIsProcessing(false));
    },
    [auth, result, startProcessing],
  );

  const changeSelect = useCallback(
    (v) => {
      if (showKDB) {
        setResultKDB((o) => o.map((r) => ({ ...r, save: v })));
      } else {
        setResult((o) => o.map((r) => ({ ...r, save: v })));
      }
    },
    [showKDB],
  );

  const rowSave = useCallback(
    (id, value) => {
      if (showKDB) {
        setResultKDB((o) => o.map((r) => (r.id === id ? { ...r, ...value } : r)));
      } else {
        setResult((o) => o.map((r) => (r.id === id ? { ...r, ...value } : r)));
      }
    },
    [showKDB],
  );

  const rowChange = useCallback(
    (id, value) => {
      setResult((o) => o.map((r) => (r.id === id ? { ...r, ...value, save: true } : r)));
    },
    [],
  );

  const checkKDB = resultKDB && resultKDB.filter((res) => res.save === true).length > 0;
  const checkWarnings = result && result.filter((res) => res.save === true).length > 0;

  const currentKDB = resultKDB && resultKDB.filter((res) => res.save === true).map((res) => (
    <SpanKDB key={res.id}>
      {res.kdb.code}
      &sbquo;&nbsp;
    </SpanKDB>
  ));

  return (
    <>
      {isProcessing && (
        <Loader text="Завантаження" />
      )}
      {err && (
        <Alert severity="error" onClose={() => setErr(null)}>
          <AlertTitle>
            {err}
          </AlertTitle>
        </Alert>
      )}
      <Box>
        <CommandPanel>
          <GenerateButton
            content="Почати обробку"
            onClick={startProcessing}
            disabled={(currentUser.org && !currentUser.org.repr) || !currentOrg || !currentDate}
          />
          <OKButton
            content="Відібрати за КДБ"
            onClick={getWarning}
            disabled={!checkKDB}
          />
          <SelectAllButton
            onClick={() => changeSelect(true)}
            addText
          />
          <RemoveSelectionButton
            onClick={() => changeSelect(false)}
            addText
          />
          <SaveButton
            content="Записати"
            onClick={saveChanges}
            disabled={showKDB || !checkWarnings}
          />
        </CommandPanel>
        {!message && resultKDB && showKDB
          && (
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Відібрати</TableCell>
                    <TableCell>КДБ код</TableCell>
                    <TableCell>КДБ назва</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resultKDB.map((row) => (
                    <RowKDB
                      key={row.id}
                      row={row}
                      rowSave={rowSave}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        {!message && result && !showKDB
          && (
            <>
              <ContainerKDB>
                <span>КДБ:&nbsp;</span>
                {currentKDB}
              </ContainerKDB>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell rowSpan="2">Записати</TableCell>
                      <TableCell width="40%">Платник</TableCell>
                      <TableCell width="40%">Договір</TableCell>
                      <TableCell>Сума до розмежування</TableCell>
                      <TableCell>
                        Сума після розмежування
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan="4">
                        Примітка
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.map((row) => (
                      <Row
                        key={row.id}
                        row={row}
                        rowChange={rowChange}
                        rowSave={rowSave}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

        {message && (
        <EditorContainer style={{ overflow: 'auto' }}>
          {message && (
          <MessageContainer>
            <img width="130px" src="https://img.icons8.com/clouds/2x/open-envelope.png" alt="" />
            <MessageText>{message}</MessageText>
          </MessageContainer>
          )}
        </EditorContainer>
        )}
      </Box>
    </>
  );
}

Warnings.propTypes = {
  currentOrg: PropTypes.shape(),
  currentDate: PropTypes.string.isRequired,
};

Warnings.defaultProps = {
  currentOrg: { id: null },
};

export default withAuthConsumer(mapStateAuth)(Warnings);
